"use client";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import React from "react";
import { ButtonStyleType, SpacingType, sectionVariants } from "@/types";
import Link from "next/link";
import Image from '@/components/ui/Image';
import parse from "html-react-parser";
import { SectionWrapper } from "../components/SectionWraper";

interface Hero10Props {
  darkMode?: boolean;
  spacing?: SpacingType;
  variant?: sectionVariants;
  contents?: {
    badge?: string;
    title?: string;
    content?: string;
    microCopy?: string; 
    image1?: string;
    image1AltText?: string;
    image2?: string;
    image2AltText?: string;
    image3?: string;
    image3AltText?: string;
    hasButton?: boolean;
    buttonText?: string;
    buttonLink?: string;
    buttonStyle?: ButtonStyleType;
    secondaryButton?: boolean;
    secondaryButtonText?: string;
    secondaryButtonLink?: string;
    secondaryButtonStyle?: ButtonStyleType;
  };
  attributes?: React.HTMLProps<HTMLElement>;
}

const Hero10 = ({
  darkMode = false,
  spacing,
  variant = 'A',
  contents,
  attributes,
}: Hero10Props) => {

  return (
    <SectionWrapper
      spacing={spacing}
      variant={variant}
      darkMode={darkMode}
      heroSection={true}
      componentName="hero10"
      attributes={attributes}
      usedInPageNav={false}
      pageNavTitle="Hero 10"
    > 
      <div className="container"> 
        <div className="flex flex-col items-center gap-6">
          {contents?.badge && (
            <Badge variant="outline" bold>
              {contents?.badge}
            </Badge>
          )}
          {contents?.title && (
            <h1 className="text-center">
              {contents?.title}
            </h1>
          )}
          {contents?.content && (
            <div className="paragraph-lg text-muted-foreground mt-4 md:mt-8 text-center max-w-2xl">
              {parse(contents?.content)}
            </div>
          )}
          {contents?.hasButton && (
            <div className="flex flex-col md:flex-row gap-4 mt-2">
              <div className="flex flex-col">
                <Button asChild variant={contents?.buttonStyle} size="lg" className="w-full sm:w-auto">
                  <Link href={contents?.buttonLink ?? ""}>
                    {contents?.buttonText}
                  </Link>
                </Button>
                {contents?.microCopy && (
                  <p className="paragraph-sm text-muted-foreground text-center mt-2">
                    {contents?.microCopy}
                  </p>
                )}
              </div>
              {contents?.secondaryButton && (
                <Button asChild variant={contents?.secondaryButtonStyle} size="lg" className="w-full sm:w-auto">
                  <Link href={contents?.secondaryButtonLink ?? ""}>
                    {contents?.secondaryButtonText}
                  </Link>
                </Button>
              )}
            </div>
          )}
        </div>

        <div className="mt-10 md:mt-20 w-full xl:w-[80%] mx-auto grid grid-cols-11 gap-6">
          <div className="col-span-11 md:col-span-5">
            <Image
              src={contents?.image1 ?? ""}
              alt={contents?.image1AltText ?? ""}
              title={contents?.image1AltText ?? ""}
              className="h-full w-full object-cover aspect-[506/393] rounded-xl"
              width={506}
              height={393}
              priority
              fetchPriority="high"
            />
          </div>
          <div className="col-span-3 hidden md:block">
            <Image
              src={contents?.image2 ?? ""}
              alt={contents?.image2AltText ?? ""}
              title={contents?.image2AltText ?? ""}
              className="h-full w-full object-cover aspect-[293/393] rounded-xl"
              width={293}
              height={393}
              priority
              fetchPriority="high"
            />
          </div>
          <div className="col-span-3 hidden md:block">
            <Image
              src={contents?.image3 ?? ""}
              alt={contents?.image3AltText ?? ""}
              title={contents?.image3AltText ?? ""}
              className="h-full w-full object-cover aspect-[293/393] rounded-xl"
              width={293}
              height={393}
              priority
              fetchPriority="high"
            />
          </div>
        </div>
      </div>
    </SectionWrapper>
  );
};

export { Hero10 };   
